import { startTransition, useEffect, useState } from 'react';
import { type WithTestId } from '@pkgs/components/types/types';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { type BigDecimalRangeDto, CURRENCY_SYMBOL_MAP, PropertyMarketingType } from '@ev-tech/search-modules-api';

import { type FilterDropdownProps } from './FilterDropdown.types';
import { PriceInput } from '../RangeInputs/PriceInput';
import { RangeDropdown } from '../RangeDropdown/RangeDropdown';
import { formatRangeLabel } from '../../utils/stringUtils';
import { validateRangeValues } from '../../utils/numberFormatters';
import { useAppContext } from '../../hooks/useAppContext';

export type PriceFilterDropdownProps = WithTestId<FilterDropdownProps>;

export const PriceFilterDropdown = ({ className, 'data-testid': dataTestId }: PriceFilterDropdownProps) => {
  const {
    t,
    options: { currency, language },
    featureFlags,
  } = useAppContext();

  const marketingType = useSearchStore((state) => state.marketingType);
  const price = useSearchStore((state) => state.price);
  const setPrice = useSearchStore((state) => state.setPrice);

  const [range, setRange] = useState<BigDecimalRangeDto | undefined>(price ? validateRangeValues(price) : undefined);

  const priceLabel = marketingType === PropertyMarketingType.rent ? t('search.searchbar.rent') : t('search.searchbar.price');
  const currencySymbol = CURRENCY_SYMBOL_MAP[currency];

  useEffect(() => {
    setRange(price ? validateRangeValues(price) : undefined);
  }, [price]);

  return (
    <RangeDropdown
      className={className}
      range={range}
      maxWidth={featureFlags.isAiSearchEnabled ? 112 : 212}
      label={priceLabel}
      value={
        price
          ? formatRangeLabel({
              value: price,
              unit: currencySymbol,
              language,
              shouldFormatNumbers: true,
              isCurrency: true,
              currency,
            })
          : ''
      }
      onDone={() => {
        if (range) startTransition(() => setPrice(range));
      }}
      onReset={() => {
        setRange(undefined);
        if (price !== undefined) {
          startTransition(() => setPrice(undefined));
        }
      }}
      data-testid={dataTestId ? `${dataTestId}_dropdown` : 'search-components_price-filter_dropdown'}
    >
      <PriceInput
        hasLabelHidden={true}
        currencySymbol={currencySymbol}
        propertyMarketingType={marketingType}
        defaultRange={range}
        language={language}
        onChange={setRange}
        data-testid={dataTestId}
      />
    </RangeDropdown>
  );
};
